import React, { Component } from 'react';
import { Form, Field, FormSpy } from 'react-final-form';
import moment from 'moment';
import pageIconSrc from '../../../src/assets/images/passport-circle.svg';
import arrowLeftSrc from '../../../src/assets/images/arrow-left.svg';
import IndexLink from '../partials/IndexLink';
import { ReactSVG } from 'react-svg';
import { connect } from 'react-redux';
import {
  messagesSelector,
  currentThemeSelector,
  identityDocumentStatusSelector
} from '../../helpers/selectors';
import { getMessage as t, trimFields } from '../../utils/utils';
import classnames from 'classnames';
import { PureFileField } from './fields/file';
import { scrollToErrorInfo } from '../../helpers/scrollToError';
import { documentPresent } from '../../helpers/components';
import { DOCUMENT_STATUS } from '../../constants/backend';
import _forEach from 'lodash/forEach';
import { PureTextField } from './fields/text';
import { PureDateField } from './fields/datePicker';
import { roundSwitch } from '../forms/newFields';

function validate(values = {}, props = {}) {
  const errors = {};
  const mandatoryFields = ['documentNumber', 'deliveranceDate'];

  values = trimFields({ ...values });

  _forEach(mandatoryFields, (field) => {
    if (!values[field]) errors[field] = true;
  });

  if (values.expirationDatePresent && !values.expirationDate) {
    errors.expirationDate = true;
  }

  if (!props.hideFileUpload) {
    if (!documentPresent(values.documentFront)) errors.documentFront = true;
    if (!documentPresent(values.documentBack)) errors.documentBack = true;
  }

  return errors;
}

/**
 * @typedef {object} IdentityDocumentProps
 * @property {boolean} [isSubscribePage=false]
 */
class IdentityDocument extends Component {
  constructor(props) {
    super(props);
    this.lastSubmitFailed = false;
    this.today = moment().startOf('day').toDate();
    this.handleSubmitFailedChange = this.handleSubmitFailedChange.bind(this);
  }

  handleSubmitFailedChange({ submitFailed }) {
    if (submitFailed && !this.lastSubmitFailed) {
      scrollToErrorInfo();
    }

    this.lastSubmitFailed = submitFailed;
  }

  getSubmitButtonLabel() {
    const { isSubscribePage, m } = this.props;

    return isSubscribePage ? t(m, 'generic.next') : t(m, 'generic.save');
  }

  displayBackButton() {
    const { isSubscribePage } = this.props;

    if (!isSubscribePage) {
      return (
        <IndexLink to="/profile" className="scope-content-header-button-return">
          <ReactSVG className="react-svg" src={arrowLeftSrc} />
        </IndexLink>
      );
    }
  }

  render() {
    const {
      m,
      onSubmit,
      initialValues,
      userDataUpdating,
      hideFileUpload,
      theme,
      identityDocumentStatus,
      isSubscribePage
    } = this.props;

    const { identityDocument } = theme.icons || {};
    const identityDocumentApproved =
      identityDocumentStatus === DOCUMENT_STATUS.APPROVED;
    const readOnlyForm = !isSubscribePage && identityDocumentApproved;
    const boundValidate = (values) => validate(values, this.props);

    return (
      <Form onSubmit={onSubmit} initialValues={initialValues} validate={boundValidate}>
        {({ handleSubmit, values }) => (
          <form className="identity-document-page" onSubmit={handleSubmit} noValidate>
            <FormSpy
              subscription={{ submitFailed: true }}
              onChange={this.handleSubmitFailedChange}
            />
            <div className="scope-content-header">
              <img
                className="scope-content-header-picture"
                src={identityDocument ? identityDocument : pageIconSrc}
                alt="page icon"
              />
              <div className="scope-content-header-title-container">
                {this.displayBackButton()}
                <div className="scope-content-header-title">
                  {t(m, 'label.identity_card')}
                </div>
              </div>
            </div>
            <div className="scope-content-body">
              <div className="scope-section">
                <div className="scope-section-fields">
                  <Field
                    name="documentNumber"
                    label={t(m, 'label.identity.document.number')}
                    placeholder={t(m, 'label.identity.document.number')}
                    mandatory
                    readOnly={readOnlyForm}
                    component={PureTextField}
                  />
                  <Field
                    name="deliveranceDate"
                    placeholder={t(m, 'document.issue.date')}
                    label={t(m, 'document.issue.date')}
                    mandatory
                    disabled={readOnlyForm}
                    maxDate={this.today}
                    component={PureDateField}
                  />
                  <Field
                    name="deliveranceCity"
                    label={t(m, 'label.delivery_place')}
                    placeholder={t(m, 'label.delivery_place')}
                    readOnly={readOnlyForm}
                    component={PureTextField}
                  />
                  {values.expirationDatePresent && (
                    <Field
                      name="expirationDate"
                      placeholder={t(m, 'label.expiration_date')}
                      label={t(m, 'label.expiration_date')}
                      mandatory
                      disabled={readOnlyForm}
                      minDate={this.today}
                      component={PureDateField}
                    />
                  )}
                  {!readOnlyForm && (
                    <div className="scope-field-container">
                      <div className="scope-boolean-container">
                        <span className="scope-boolean-info">
                          {t(m, 'label.has_expiration_date')}
                        </span>
                        <Field
                          name="expirationDatePresent"
                          type="checkbox"
                          component={roundSwitch}
                        />
                      </div>
                    </div>
                  )}
                  {hideFileUpload && (
                    <div className="scope-field-container">
                      <div className="scope-info-text">
                        {t(m, 'label.file.upload.only.mobile')}
                      </div>
                    </div>
                  )}
                  {!hideFileUpload && (
                    <Field
                      name="documentFront"
                      label={t(m, 'label.identity_card_front')}
                      mandatory
                      disabled={readOnlyForm}
                      component={PureFileField}
                    />
                  )}
                  {!hideFileUpload && (
                    <Field
                      name="documentBack"
                      label={t(m, 'label.identity_card_back')}
                      mandatory
                      disabled={readOnlyForm}
                      component={PureFileField}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="scope-content-footer">
              {!readOnlyForm && (
                <button
                  type="submit"
                  className={classnames('scope-submit-button', 'primary-btn', {
                    'sub-class-disabled': userDataUpdating
                  })}
                >
                  {this.getSubmitButtonLabel()}
                </button>
              )}
            </div>
          </form>
        )}
      </Form>
    );
  }
}

IdentityDocument.defaultProps = {
  isSubscribePage: false
};


const mapStateToProps = (state) => {
  const { userDataUpdating } = state.user;

  return {
    m: messagesSelector(state),
    userDataUpdating,
    identityDocumentStatus: identityDocumentStatusSelector(state),
    theme: currentThemeSelector(state)
  };
};

export default connect(mapStateToProps)(IdentityDocument);
